import { supabase } from '@/lib/supabase';
import { callApi } from '@/utils/api';
import { getActiveTeamQueryKey, getTeamsQueryKey } from '@/utils/query-keys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const AccountStatus = {
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
} as const;

export type AccountStatusType =
  (typeof AccountStatus)[keyof typeof AccountStatus];

export type Team = {
  id: string;
  name: string;
  audience: string | null;
  description: string | null;
  industry: string | null;
  tone_and_style: string | null;
  website_url: string | null;
  posting_frequency: number | null;
  posting_frequency_unit: string | null;
  brand_voice: string | null;
  nylas_grant_id: string | null;
  selected_facebook_account_id: string | null;
  created_by: string | null;
  created_at: string | null;
  subscription: any | null;
  product: any | null;
  stripeCustomerId: string | null;
  facebook_account: {
    id: string;
    account_id: string;
    insights: Array<{
      id: string;
      facebook_account_id: string;
      name: string;
      title: string;
      description: string;
      timestamp: string;
      value: {
        value: number;
        end_time: string;
      };
    }>;
    lastProcessed: string;
    status: AccountStatusType;
    team_id: string;
  } | null;
  tokens: {
    facebook_token: string | null;
  };
};

export const useTeams = () => {
  const queryClient = useQueryClient();

  const query = useQuery({
    gcTime: 0,
    staleTime: 0,
    queryKey: getTeamsQueryKey(),
    queryFn: async (): Promise<Team[]> => {
      const { data, error } = await callApi(supabase, '/teams/all');

      if (error) throw error;

      return data;
    },
  });

  const activeTeam = useQuery({
    queryKey: getActiveTeamQueryKey(),
    queryFn: async (): Promise<Team> => {
      const { data, error } = await callApi(supabase, '/teams');

      if (error) throw error;

      return data;
    },
    refetchInterval: (currentQuery) =>
      currentQuery.state.data?.facebook_account?.status ===
      AccountStatus.PROCESSING
        ? 30000
        : false,
  });

  const createTeam = useMutation({
    mutationFn: async (data: { name: string }): Promise<Team> => {
      const { data: newTeam, error } = await callApi(supabase, '/teams', {
        method: 'POST',
        data,
      });

      if (error) throw error;
      if (!newTeam) throw new Error('Failed to create team');

      return newTeam;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getTeamsQueryKey() });
    },
  });

  const deleteTeam = useMutation({
    mutationFn: async (teamId: string) => {
      const { error } = await callApi(supabase, `/teams/${teamId}`, {
        method: 'DELETE',
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getTeamsQueryKey() });
    },
  });

  const updateTeam = useMutation({
    mutationFn: async ({
      id,
      ...data
    }: Partial<Team> & { id: string }): Promise<Team> => {
      const { data: updatedTeam, error } = await callApi(
        supabase,
        `/teams/${id}`,
        {
          method: 'PUT',
          data,
        },
      );

      if (error) throw error;
      if (!updatedTeam) throw new Error('Failed to update team');

      return updatedTeam;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getTeamsQueryKey() });
      queryClient.invalidateQueries({ queryKey: getActiveTeamQueryKey() });
    },
  });

  return {
    ...query,
    activeTeam,
    createTeam,
    deleteTeam,
    updateTeam,
  };
};
